import http from '@/http-common'
import { stripInvalidId } from '@/services/utils'
import type {
  AutosaveDraftPayload,
  AutosaveDraftResponse,
  AvailableReportsResponse,
  EntityType,
  ExecutionDownloadUrlResponse,
  ExecutionStatusResponse,
  PaginatedResponse,
  PreviewPayload,
  PreviewResponse,
  PublishPayload,
  PublishResponse,
  ReportPreviewContextOptionsResponse,
  RenderPayload,
  RenderResponse,
  ReportExecution,
  ReportExecutionListResponse,
  ReportTemplate,
  ReportTemplateBinding,
  ReportTemplateBindingsResponse,
  ReportTemplateDraftResponse,
  ReportTemplateListResponse,
  ReportVariablesResponse,
  RollbackPayload,
  VersionsResponse,
} from '@/types/reports'

const normalizeListResponse = <T>(data: any): PaginatedResponse<T> => {
  if (!data) return { results: [] }

  if (Array.isArray(data)) {
    return { results: data as T[] }
  }

  if (Array.isArray(data.results)) {
    return data as PaginatedResponse<T>
  }

  // Some endpoints may return { items: [...] }
  if (Array.isArray(data.items)) {
    return { results: data.items as T[] }
  }

  return { results: [] }
}

class ReportsService {
  // Discovery: which templates apply to a given entity detail screen.
  getAvailable(entity: EntityType, entityId?: string | number | null): Promise<{ data: AvailableReportsResponse }> {
    const params = new URLSearchParams({ entity })
    if (entityId !== null && entityId !== undefined && entityId !== '') {
      params.set('entity_id', String(entityId))
    }
    return http.get(`/reports/available/?${params.toString()}`)
  }

  // Variables catalog
  getVariables(param?: string): Promise<{ data: ReportVariablesResponse }> {
    const qs = param ? (param.startsWith('?') ? param : `?${param}`) : ''
    return http.get(`/variables/${qs}`)
  }

  // Templates
  listTemplates(param?: string): Promise<{ data: ReportTemplateListResponse }> {
    const qs = param ? (param.startsWith('?') ? param : `?${param}`) : ''
    return http.get(`/report-templates/${qs}`).then((resp: any) => {
      resp.data = normalizeListResponse<ReportTemplate>(resp.data)
      return resp
    })
  }

  getTemplate(templateId: string | number): Promise<{ data: ReportTemplate }> {
    return http.get(`/report-templates/${templateId}/`)
  }

  getDraft(templateId: string | number): Promise<{ data: ReportTemplateDraftResponse }> {
    return http.get(`/report-templates/${templateId}/draft/`)
  }

  getBindings(templateId: string | number): Promise<{ data: ReportTemplateBindingsResponse }> {
    return http.get(`/report-templates/${templateId}/bindings/`)
  }

  replaceBindings(
    templateId: string | number,
    bindings: ReportTemplateBinding[],
  ): Promise<{ data: ReportTemplateBindingsResponse }> {
    return http.put(`/report-templates/${templateId}/bindings/`, { bindings })
  }

  createTemplate(payload: Partial<ReportTemplate>): Promise<{ data: ReportTemplate }> {
    return http.post('/report-templates/', payload)
  }

  updateTemplate(templateId: string | number, payload: Partial<ReportTemplate>): Promise<{ data: ReportTemplate }> {
    const cleanPayload = stripInvalidId(payload)
    return http.patch(`/report-templates/${templateId}/`, cleanPayload)
  }

  deleteTemplate(templateId: string | number): Promise<any> {
    return http.delete(`/report-templates/${templateId}/`)
  }

  autosaveDraft(templateId: string | number, payload: AutosaveDraftPayload): Promise<{ data: AutosaveDraftResponse }> {
    return http.post(`/report-templates/${templateId}/draft/autosave/`, payload)
  }

  publish(templateId: string | number, payload?: PublishPayload): Promise<{ data: PublishResponse }> {
    return http.post(`/report-templates/${templateId}/publish/`, payload || {})
  }

  rollback(templateId: string | number, payload: RollbackPayload): Promise<{ data: PublishResponse }> {
    return http.post(`/report-templates/${templateId}/rollback/`, payload)
  }

  getVersions(templateId: string | number): Promise<{ data: VersionsResponse }> {
    return http.get(`/report-templates/${templateId}/versions/`)
  }

  preview(templateId: string | number, payload: PreviewPayload): Promise<{ data: PreviewResponse }> {
    return http.post(`/report-templates/${templateId}/preview/`, payload)
  }

  getPreviewContextOptions(
    templateId: string | number,
    entityType: EntityType,
    search = '',
  ): Promise<{ data: ReportPreviewContextOptionsResponse }> {
    const params = new URLSearchParams({ entity_type: entityType })
    if (search.trim()) params.set('search', search.trim())
    return http.get(`/report-templates/${templateId}/preview-context-options/?${params.toString()}`)
  }

  render(templateId: string | number, payload: RenderPayload): Promise<{ data: RenderResponse }> {
    return http.post(`/report-templates/${templateId}/render/`, payload)
  }

  // Executions
  listExecutions(param?: string): Promise<{ data: ReportExecutionListResponse }> {
    const qs = param ? (param.startsWith('?') ? param : `?${param}`) : ''
    return http.get(`/report-executions/${qs}`).then((resp: any) => {
      resp.data = normalizeListResponse<ReportExecution>(resp.data)
      return resp
    })
  }

  getExecution(executionId: string | number): Promise<{ data: ReportExecution }> {
    return http.get(`/report-executions/${executionId}/`)
  }

  getExecutionStatus(
    executionId: string | number,
    includeDownloadUrl?: boolean,
  ): Promise<{ data: ExecutionStatusResponse }> {
    const qs = includeDownloadUrl ? '?include_download_url=1' : ''
    return http.get(`/report-executions/${executionId}/status/${qs}`)
  }

  getExecutionDownloadUrl(executionId: string | number): Promise<{ data: ExecutionDownloadUrlResponse }> {
    return http.get(`/report-executions/${executionId}/download/`)
  }
}

export default new ReportsService()
